import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { lotOffageRecordsApi } from 'api/inventory';
import colors from 'common/constants/colors';
import { formatFacilityWithState } from 'common/utils/facilityLabel';

import formatOffageReasons from '../utils/formatOffageReasons';

const TITLE_ID = 'lot-offage-records-title';
const RECORD_COLUMNS = ['DATE', 'STAGE', 'QTY', 'REASONS', 'RECORDED BY', 'NOTES'];

const DashWhenEmpty = ({ text }) =>
  text || (
    <Box component='span' sx={{ color: 'text.disabled' }}>
      —
    </Box>
  );
DashWhenEmpty.propTypes = { text: PropTypes.string };
DashWhenEmpty.defaultProps = { text: '' };

const describeRecorder = (record) => {
  if (record.isRecordedBySystem) return 'System';
  return record.recordedByName ?? '';
};

const OffageStat = ({ label, value, testId }) => (
  <Box data-testid={testId}>
    <Typography
      variant='caption'
      sx={{ display: 'block', color: 'text.secondary', letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{value}</Typography>
  </Box>
);
OffageStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  testId: PropTypes.string.isRequired,
};

/**
 * POM-6561 - what one lot lost, record by record. Opened from the lot's OFFAGE number in the Lot
 * Summary drawer; open while `lot` is set.
 */
const LotOffageRecordsDialog = ({ lot, item, onClose }) => {
  const [offage, setOffage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const latestRequestRef = useRef(0);
  // The drawer clears `lot` on close; the dialog keeps showing that lot while it fades out.
  const lastOpenedLotRef = useRef(lot);
  if (lot) lastOpenedLotRef.current = lot;
  const shownLot = lot ?? lastOpenedLotRef.current;
  const inventoryLotId = lot?.inventoryLotId;

  const loadRecords = useCallback(() => {
    latestRequestRef.current += 1;
    if (!inventoryLotId) return;
    // A slow answer for a lot opened earlier must not land under the lot open now.
    const requestNumber = latestRequestRef.current;
    const isLatestRequest = () => requestNumber === latestRequestRef.current;
    setIsLoading(true);
    setHasLoadFailed(false);
    lotOffageRecordsApi({ inventoryLotId })
      .then((result) => isLatestRequest() && setOffage(result))
      .catch(() => isLatestRequest() && setHasLoadFailed(true))
      .finally(() => isLatestRequest() && setIsLoading(false));
  }, [inventoryLotId]);

  useEffect(() => {
    if (inventoryLotId) setOffage(null);
    loadRecords();
  }, [inventoryLotId, loadRecords]);

  const clearAfterClose = () => {
    setOffage(null);
    setIsLoading(false);
    setHasLoadFailed(false);
  };

  const records = offage?.records ?? [];

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (hasLoadFailed) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <Typography color='error'>Could not load the offage records.</Typography>
          <Button variant='outlined' size='small' onClick={loadRecords}>
            Retry
          </Button>
        </Box>
      );
    }
    if (records.length === 0) {
      return (
        <Typography color='text.secondary' sx={{ py: 4 }}>
          No offage recorded for this lot.
        </Typography>
      );
    }
    return (
      <>
        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
          <OffageStat
            label='TOTAL LOST'
            value={offage.totalOffageQty}
            testId='offage-stat-total-lost'
          />
          <OffageStat label='RECORDS' value={records.length} testId='offage-stat-records' />
          <OffageStat
            label='REMAINING NOW'
            value={shownLot?.remainingQty ?? 0}
            testId='offage-stat-remaining-now'
          />
        </Box>
        <TableContainer>
          <Table size='small' data-testid='offage-records-table'>
            <TableHead>
              <TableRow>
                {RECORD_COLUMNS.map((column) => (
                  <TableCell
                    key={column}
                    align={column === 'QTY' ? 'right' : 'left'}
                    sx={{ fontWeight: 600, fontSize: 11, letterSpacing: 0.5 }}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Newest first, as the server sends them. */}
              {records.map((record) => (
                <TableRow
                  key={record.adjustmentId}
                  data-testid={`offage-record-${record.adjustmentId}`}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(record.recordedAt), 'MM/dd/yyyy HH:mm')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{record.stageLabel}</TableCell>
                  <TableCell align='right' sx={{ fontWeight: 700 }}>
                    {record.qty}
                  </TableCell>
                  <TableCell>
                    <DashWhenEmpty text={formatOffageReasons(record.offageReasons, record.qty)} />
                  </TableCell>
                  <TableCell>
                    <DashWhenEmpty text={describeRecorder(record)} />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    <DashWhenEmpty text={record.notes ?? ''} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Dialog
      open={!!lot}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      aria-labelledby={TITLE_ID}
      TransitionProps={{ onExited: clearAfterClose }}
    >
      <DialogTitle
        id={TITLE_ID}
        sx={{ bgcolor: colors.dialogHeaderNavy, color: colors.white, pr: 6 }}
      >
        <Typography variant='h6' component='div' fontWeight={700}>
          Offage records ·{' '}
          <Box component='span' sx={{ fontFamily: 'monospace' }}>
            {shownLot?.lotNumber}
          </Box>
        </Typography>
        <Typography variant='body2' component='div' sx={{ color: colors.dialogHeaderSubtext }}>
          {item.drugName} · NDC {item.ndc} ·{' '}
          {formatFacilityWithState(item.fulfillingPharmacy?.name, item.fulfillingPharmacy?.state)}
        </Typography>
      </DialogTitle>
      {/* Outside the title, which names the dialog: inside, "Close" became part of its name. */}
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: colors.white }}
        aria-label='Close offage records'
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>{renderBody()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

LotOffageRecordsDialog.propTypes = {
  lot: PropTypes.shape({
    inventoryLotId: PropTypes.string,
    lotNumber: PropTypes.string,
    remainingQty: PropTypes.number,
  }),
  item: PropTypes.shape({
    drugName: PropTypes.string,
    ndc: PropTypes.string,
    fulfillingPharmacy: PropTypes.shape({ name: PropTypes.string, state: PropTypes.string }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

LotOffageRecordsDialog.defaultProps = {
  lot: null,
};

export default LotOffageRecordsDialog;
